<template>
  <div class="min-h-full p-4">
    <div class="container">
      <button
        type="button"
        @click="handleBack"
        class="mb-6 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white px-3 py-1.5 rounded-xl border border-indigo-400 border-opacity-20 font-medium transition-all duration-300 flex items-center gap-2 hover:from-indigo-600 hover:to-indigo-700"
      >
        <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        返回
      </button>

      <div class="header">
        <div class="inline-flex items-center justify-center w-16 h-16 gradient-button rounded-full mb-4 shadow-lg">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
        </div>
        <h1 class="title">批量查询计价说明</h1>
        <p class="subtitle">超出免费额度后，按阶梯价格计费，量越大越优惠</p>
      </div>

      <!-- 免费额度提示 -->
      <div class="free-banner">
        <div class="free-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
        </div>
        <div>
          <div class="free-title">免费额度</div>
          <div class="free-desc">每天首次批量查询前 <strong>{{ freeBatchLimit }}</strong> 条数据免费处理，无需支付</div>
        </div>
      </div>

      <!-- 阶梯价格表 -->
      <div class="main-card">
        <h2 class="section-title">阶梯价格表</h2>
        <p class="section-desc">超出免费额度的部分，按以下阶梯累计计价</p>

        <div class="pricing-table">
          <div class="table-header">
            <div class="col-tier">档位</div>
            <div class="col-range">数量范围（超出免费额度部分）</div>
            <div class="col-price">单价</div>
          </div>

          <div class="table-row" v-for="(tier, index) in tiers" :key="index">
            <div class="col-tier">
              <span class="tier-badge" :class="'tier-' + (index + 1)">{{ index + 1 }}</span>
            </div>
            <div class="col-range">{{ tier.range }}</div>
            <div class="col-price">
              <span class="price-tag">{{ tier.price }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 计价示例 -->
      <div class="main-card">
        <h2 class="section-title">计价示例</h2>
        <p class="section-desc">假设免费额度为 {{ freeBatchLimit }} 条</p>

        <div class="examples">
          <div class="example-item" v-for="(example, index) in examples" :key="index">
            <div class="example-header">
              <span class="example-count">{{ example.count }} 条数据</span>
              <span class="example-price">¥{{ example.price }}</span>
            </div>
            <div class="example-detail">{{ example.detail }}</div>
          </div>
        </div>
      </div>

      <!-- 说明 -->
      <div class="main-card">
        <h2 class="section-title">计费说明</h2>
        <div class="notes">
          <div class="note-item">
            <span class="note-dot"></span>
            价格按阶梯<strong>累计</strong>计算，非全量按最高档计费
          </div>
          <div class="note-item">
            <span class="note-dot"></span>
            免费额度每天限用一次；当天已有待支付或已支付的批量订单即视为已消耗，后续不再免费；已取消、已退款等不占用额度
          </div>
          <div class="note-item">
            <span class="note-dot"></span>
            实际金额保留两位小数，四舍五入
          </div>
          <div class="note-item">
            <span class="note-dot"></span>
            支持支付宝在线支付，支付后自动处理文件
          </div>
        </div>
      </div>

      <div class="footer-info">
        支持 MD5、SHA256 与国密 SM3 摘要解析 · 安全可靠的数据处理
      </div>
    </div>
  </div>
</template>

<script setup>
import { useRouter } from 'vue-router';

const router = useRouter();
const freeBatchLimit = 100;

const tiers = [
  { range: '1 ~ 1,000 条', price: '2 元 / 千条' },
  { range: '1,001 ~ 10,000 条', price: '1.8 元 / 千条' },
  { range: '10,001 ~ 500,000 条', price: '1.5 元 / 千条' },
  { range: '500,001 ~ 1,000,000 条', price: '1.1 元 / 千条' },
  { range: '1,000,000 条以上', price: '0.9 元 / 千条' },
];

const examples = [
  {
    count: '50',
    price: '0',
    detail: `未超出免费额度（${freeBatchLimit} 条），免费`,
  },
  {
    count: '600',
    price: '1.00',
    detail: `超出 ${freeBatchLimit} 条免费额度，需付费 500 条 → 第一档 500/1000 × 2 = 1.00 元`,
  },
  {
    count: '5,100',
    price: '9.20',
    detail: '付费 5,000 条 → 第一档 1000 条 2 元 + 第二档 4000/1000 × 1.8 = 7.2 元，合计 9.20 元',
  },
  {
    count: '20,100',
    price: '33.20',
    detail: '付费 20,000 条 → 第一档 2 元 + 第二档 9×1.8 = 16.2 元 + 第三档 10000/1000×1.5 = 15 元，合计 33.20 元',
  },
];

const handleBack = () => {
  router.go(-1);
};
</script>

<style scoped>
.gradient-button {
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
}

.container {
  max-width: 800px;
  margin: 0 auto;
}

.header {
  text-align: center;
  margin-bottom: 32px;
}

.title {
  color: #1e293b;
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
}

.subtitle {
  color: #64748b;
  font-size: 15px;
}

/* 免费额度 banner */
.free-banner {
  display: flex;
  align-items: center;
  gap: 16px;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border: 1px solid #a7f3d0;
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 24px;
}

.free-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: #10b981;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.free-title {
  font-size: 16px;
  font-weight: 600;
  color: #065f46;
  margin-bottom: 4px;
}

.free-desc {
  font-size: 14px;
  color: #047857;
}

/* 卡片 */
.main-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  padding: 32px;
  margin-bottom: 24px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.section-desc {
  font-size: 14px;
  color: #94a3b8;
  margin-bottom: 24px;
}

/* 价格表 */
.pricing-table {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.table-header {
  display: flex;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  font-size: 14px;
  font-weight: 600;
  padding: 14px 20px;
}

.table-row {
  display: flex;
  padding: 16px 20px;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.2s;
}

.table-row:last-child {
  border-bottom: none;
}

.table-row:hover {
  background: #f8fafc;
}

.col-tier {
  width: 60px;
  flex-shrink: 0;
}

.col-range {
  flex: 1;
  font-size: 14px;
  color: #475569;
}

.col-price {
  width: 140px;
  flex-shrink: 0;
  text-align: right;
}

.tier-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  color: white;
}

.tier-1 { background: #6366f1; }
.tier-2 { background: #8b5cf6; }
.tier-3 { background: #3b82f6; }
.tier-4 { background: #0ea5e9; }
.tier-5 { background: #14b8a6; }

.price-tag {
  font-size: 14px;
  font-weight: 600;
  color: #6366f1;
}

/* 计价示例 */
.examples {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.example-item {
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px 20px;
  border: 1px solid #e2e8f0;
}

.example-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.example-count {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

.example-price {
  font-size: 18px;
  font-weight: 700;
  color: #6366f1;
}

.example-detail {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
}

/* 说明 */
.notes {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.note-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
}

.note-dot {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #6366f1;
  margin-top: 8px;
}

.footer-info {
  text-align: center;
  color: #999;
  font-size: 14px;
  padding: 16px 0;
}

@media (max-width: 640px) {
  .main-card {
    padding: 20px;
  }

  .table-header,
  .table-row {
    padding: 12px 14px;
  }

  .col-tier {
    width: 40px;
  }

  .col-price {
    width: 110px;
  }

  .col-range {
    font-size: 13px;
  }

  .free-banner {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
}
</style>
